import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtAddress, niceName } from "@/lib/format";
import { Search, Loader2, Home, User, X } from "lucide-react";

// Pesquisa do cabeçalho: processos (morada, referência, clientes) e contactos.
export function GlobalSearch({ dark }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const input = useRef(null);
  const box = useRef(null);
  const seq = useRef(0);
  useEffect(() => {
    const s = q.trim();
    setRes((r) => (r && r.q === s ? r : null));
    if (s.length < 2) { setBusy(false); return undefined; }
    setBusy(true);
    const n = ++seq.current;
    const t = setTimeout(() => {
      Promise.all([api.get("/processes", { params: { search: s, scope: "all" } }).catch(() => ({ data: [] })),
        api.get("/contacts", { params: { q: s } }).catch(() => ({ data: [] }))])
        .then(([p, c]) => { if (n !== seq.current) return; setRes({ q: s, procs: p.data.slice(0, 5), contacts: c.data.slice(0, 4) }); setBusy(false); });
    }, 250);
    return () => { clearTimeout(t); };
  }, [q]);
  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { document.removeEventListener("mousedown", close); };
  }, []);
  useEffect(() => {
    if (wide) input.current?.focus();
    document.body.classList.toggle("search-open", wide);
    return () => { document.body.classList.remove("search-open"); };
  }, [wide]);
  const go = (to) => { setOpen(false); setWide(false); setQ(""); navigate(to); };
  const shut = () => { setOpen(false); setWide(false); setQ(""); };
  const empty = res && !res.procs.length && !res.contacts.length;
  const pos = wide ? `fixed inset-0 z-[70] px-3 py-3 overflow-y-auto ${dark ? "" : "bg-white"} xl:static xl:p-0 xl:overflow-visible xl:bg-transparent` : "relative hidden";
  return (
    <>
    <button type="button" onClick={() => setWide(true)} aria-label="Pesquisar" data-testid="global-search-open"
      className={`xl:hidden w-9 h-9 grid place-items-center rounded-full transition-colors ${dark ? "text-white/80 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"}`}>
      <Search size={19} />
    </button>
    <div ref={box} className={`${pos} xl:relative xl:block xl:w-[260px] 2xl:w-[300px]`} style={wide && dark ? { backgroundColor: "var(--hc)" } : undefined} data-testid="global-search">
      <div className="relative flex items-center gap-2">
      <div className="relative flex-1 min-w-0">
      <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${dark ? "text-white/60" : "text-slate-400"}`} />
      <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Escape") shut(); if (e.key === "Enter" && res?.procs[0]) go(`/processo/${res.procs[0].id}`); }}
        placeholder="Processos, contactos…" data-testid="global-search-input"
        className={`w-full h-10 pl-9 pr-9 rounded-xl text-sm outline-none transition-colors ${dark ? "bg-white/10 text-white placeholder:text-white/55 border border-white/15 focus:bg-white/15 focus:border-white/40" : "bg-slate-50 border border-slate-200 text-slate-800 focus:border-slate-400"}`} />
      {busy && <Loader2 size={15} className={`absolute right-3 top-1/2 -translate-y-1/2 animate-spin ${dark ? "text-white/70" : "text-slate-400"}`} />}
      </div>
      {wide && <button type="button" onClick={shut} aria-label="Fechar pesquisa" data-testid="global-search-close"
        className={`xl:hidden w-10 h-10 shrink-0 grid place-items-center rounded-full ${dark ? "text-white hover:bg-white/10" : "text-slate-600 hover:bg-slate-100"}`}><X size={20} /></button>}
      </div>
      {wide && q.trim().length < 2 && <p className={`xl:hidden px-2 pt-4 text-sm ${dark ? "text-white/70" : "text-slate-500"}`} data-testid="global-search-hint">Procure por morada, referência, cliente ou contacto.</p>}
      {open && res && (
        <div className="relative xl:absolute xl:right-0 mt-3 xl:mt-2 xl:w-[380px] xl:max-h-[70vh] xl:overflow-y-auto bg-white text-slate-800 rounded-xl shadow-[0_18px_44px_rgb(0,0,0,0.18)] border border-slate-100 py-2 z-[60]" data-testid="global-search-results">
          {empty && <p className="px-4 py-3 text-sm text-slate-500" data-testid="global-search-empty">Nada encontrado para «{q.trim()}».</p>}
          {res.procs.length > 0 && <Group title="Processos">{res.procs.map((p) => (
            <Row key={p.id} icon={Home} onClick={() => go(`/processo/${p.id}`)} testid={`global-search-proc-${p.ref}`}
              title={p.card?.address || fmtAddress(p.property_address) || "Morada por preencher"} sub={[p.card?.num || p.ref, p.card?.parties].filter(Boolean).join(" · ")} />
          ))}</Group>}
          {res.contacts.length > 0 && <Group title="Contactos">{res.contacts.map((c) => (
            <Row key={c.id} icon={User} onClick={() => go(`/contactos?q=${encodeURIComponent(c.name || q.trim())}`)} testid={`global-search-contact-${c.id}`}
              title={niceName(c.name) || c.entity} sub={[c.entity, c.phone || c.email].filter(Boolean).join(" · ")} />
          ))}</Group>}
        </div>
      )}
    </div>
    </>
  );
}

const Group = ({ title, children }) => (
  <div className="py-1"><div className="px-4 pb-1 text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-400">{title}</div>{children}</div>
);

const Row = ({ icon: Icon, title, sub, onClick, testid }) => (
  <button type="button" onClick={onClick} className="w-full flex items-start gap-3 px-4 py-2 text-left hover:bg-slate-50 transition-colors" data-testid={testid}>
    <Icon size={16} className="mt-0.5 text-slate-400 shrink-0" />
    <span className="min-w-0"><span className="block text-sm font-medium truncate">{title}</span>{sub && <span className="block text-xs text-slate-500 truncate">{sub}</span>}</span>
  </button>
);
